import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CATEGORIES,
  COMMISSION_TYPES,
  COMMISSION_TYPE_LABELS,
  PLATFORMS,
  PLATFORM_LABELS,
} from '@affily/shared/constants';
import { formatCount, formatPercent, formatCents } from '@affily/shared/money';
import { useFetch } from '../hooks/useFetch.js';
import { qs } from '../lib/api.js';
import { Seo } from '../components/Seo.jsx';
import { TermsInline } from '../components/Terms.jsx';
import { FilterPanel, FilterGroup, FilterChip } from '../components/Filters.jsx';
import { Badge, Spinner, EmptyState, Button, Unknown } from '../components/ui.jsx';

const SORTS = [
  { value: 'newest', label: 'Newest' },
  { value: 'rate', label: 'Highest rate' },
  { value: 'cookie', label: 'Longest cookie' },
  { value: 'recurring', label: 'Longest recurring' },
  { value: 'conversions', label: 'Most conversions' },
];

export default function ProgramsDirectory() {
  const [params, setParams] = useSearchParams();

  const query = useMemo(
    () => ({
      q: params.get('q') ?? '',
      category: params.getAll('category'),
      commissionType: params.getAll('commissionType'),
      platform: params.getAll('platform'),
      paysFor: params.getAll('paysFor'),
      recurringOnly: params.get('recurringOnly') ?? '',
      gifts: params.get('gifts') ?? '',
      minRateBps: params.get('minRateBps') ?? '',
      minCookieDays: params.get('minCookieDays') ?? '',
      sort: params.get('sort') ?? 'newest',
      page: params.get('page') ?? '1',
    }),
    [params],
  );

  const { data, loading } = useFetch(`/programs${qs(query)}`);

  function toggle(key, value) {
    const next = new URLSearchParams(params);
    const current = next.getAll(key);
    next.delete(key);
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updated.forEach((v) => next.append(key, v));
    next.delete('page');
    setParams(next);
  }

  function setValue(key, value) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next);
  }

  const programs = data?.programs ?? [];
  const featured = data?.featured ?? [];
  const pagination = data?.pagination;
  const activeFilters =
    query.category.length +
    query.commissionType.length +
    query.platform.length +
    query.paysFor.length +
    (query.recurringOnly ? 1 : 0) +
    (query.gifts ? 1 : 0) +
    (query.minRateBps ? 1 : 0) +
    (query.minCookieDays ? 1 : 0);

  return (
    <>
      <Seo
        title="Partnership programs for creators"
        description="Browse brand partnership programs with fully public terms. Filter by category, commission type, rate, platform and cookie window. No application needed to see the rate."
      />

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <header className="max-w-2xl">
          <h1 className="text-4xl font-semibold tracking-tight">Partnership programs</h1>
          <p className="mt-2 text-ink-muted">
            Every program listed here has its full terms — commission, cookie window, payout
            schedule, content requirements — on its page. No account, no application gate.
          </p>
        </header>

        <div className="mt-8 flex flex-col gap-8 lg:flex-row">
          <FilterPanel
            activeCount={activeFilters}
            onClear={() => setParams(new URLSearchParams(query.sort ? { sort: query.sort } : {}))}
            searchValue={query.q}
            onSearch={(v) => setValue('q', v)}
            searchPlaceholder="Search programs"
          >
            <FilterGroup label="Commission type">
              {COMMISSION_TYPES.map((t) => (
                <FilterChip
                  key={t}
                  active={query.commissionType.includes(t)}
                  onClick={() => toggle('commissionType', t)}
                >
                  {COMMISSION_TYPE_LABELS[t]}
                </FilterChip>
              ))}
            </FilterGroup>

            <FilterGroup label="Minimum rate">
              {[
                { label: 'Any', value: '' },
                { label: '5%+', value: '500' },
                { label: '10%+', value: '1000' },
                { label: '20%+', value: '2000' },
              ].map((opt) => (
                <FilterChip
                  key={opt.label}
                  active={query.minRateBps === opt.value}
                  onClick={() => setValue('minRateBps', opt.value)}
                >
                  {opt.label}
                </FilterChip>
              ))}
            </FilterGroup>

            <FilterGroup label="Payout model">
              {[
                { label: 'Any', value: '' },
                { label: 'Recurring only', value: 'true' },
              ].map((opt) => (
                <FilterChip
                  key={opt.label}
                  active={(query.recurringOnly ?? '') === opt.value}
                  onClick={() => setValue('recurringOnly', opt.value)}
                >
                  {opt.label}
                </FilterChip>
              ))}
            </FilterGroup>

            <FilterGroup label="Gifts">
              {[
                { label: 'Any', value: '' },
                { label: 'Has gifts', value: '1' },
              ].map((opt) => (
                <FilterChip
                  key={opt.label}
                  active={query.gifts === opt.value}
                  onClick={() => setValue('gifts', opt.value)}
                >
                  {opt.label}
                </FilterChip>
              ))}
            </FilterGroup>

            <FilterGroup label="Pays for">
              {['purchase', 'subscription', 'signup', 'trial', 'lead'].map((c) => (
                <FilterChip
                  key={c}
                  active={query.paysFor.includes(c)}
                  onClick={() => toggle('paysFor', c)}
                  className="capitalize"
                >
                  {c}
                </FilterChip>
              ))}
            </FilterGroup>

            <FilterGroup label="Cookie window">
              {[
                { label: 'Any', value: '' },
                { label: '7+ days', value: '7' },
                { label: '30+ days', value: '30' },
                { label: '60+ days', value: '60' },
              ].map((opt) => (
                <FilterChip
                  key={opt.label}
                  active={query.minCookieDays === opt.value}
                  onClick={() => setValue('minCookieDays', opt.value)}
                >
                  {opt.label}
                </FilterChip>
              ))}
            </FilterGroup>

            <FilterGroup label="Platform">
              {PLATFORMS.slice(0, 8).map((p) => (
                <FilterChip
                  key={p}
                  active={query.platform.includes(p)}
                  onClick={() => toggle('platform', p)}
                >
                  {PLATFORM_LABELS[p]}
                </FilterChip>
              ))}
            </FilterGroup>

            <FilterGroup label="Category">
              {CATEGORIES.map((c) => (
                <FilterChip
                  key={c}
                  active={query.category.includes(c)}
                  onClick={() => toggle('category', c)}
                  className="capitalize"
                >
                  {c}
                </FilterChip>
              ))}
            </FilterGroup>
          </FilterPanel>

          <main className="min-w-0 flex-1">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-ink-muted">
                {loading ? 'Loading…' : `${formatCount(pagination?.total ?? 0)} programs`}
              </p>
              <label className="flex items-center gap-2 text-sm">
                <span className="text-ink-muted">Sort</span>
                <select
                  value={query.sort}
                  onChange={(e) => setValue('sort', e.target.value)}
                  className="rounded-md border border-rule-strong bg-paper-raised px-2 py-1 text-sm"
                >
                  {SORTS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Featured is a separate, labelled block. It never reorders the
                organic list below — see the API's `featured` array. */}
            {featured.length > 0 && (
              <section className="mb-6">
                <h2 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                  Featured · paid placement
                </h2>
                <div className="overflow-hidden rounded-lg border border-accent/25 bg-accent-soft/40">
                  <ProgramTable programs={featured} />
                </div>
                <p className="mt-1.5 text-xs text-ink-faint">
                  These brands pay for this slot. It does not move them in the results below.
                </p>
              </section>
            )}

            {loading ? (
              <div className="flex justify-center py-16 text-ink-faint">
                <Spinner />
              </div>
            ) : programs.length === 0 ? (
              <EmptyState title="No programs match those filters">
                Try widening the rate or cookie window filters.
              </EmptyState>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
                <ProgramTable programs={programs} />
              </div>
            )}

            {pagination && pagination.pages > 1 && (
              <nav className="mt-6 flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setValue('page', String(pagination.page - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-ink-muted">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setValue('page', String(pagination.page + 1))}
                >
                  Next
                </Button>
              </nav>
            )}
          </main>
        </div>
      </div>
    </>
  );
}

const payableCount = (p) => (p.events ?? []).filter((e) => e.isPayable).length;

/** Tables, not cards: the point is comparing terms across rows at a glance. */
function ProgramTable({ programs }) {
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Program</th>
          <th>Terms</th>
          <th className="num">Cookie</th>
          <th className="num">AOV</th>
          <th className="num">Conv. rate</th>
          <th>Platforms</th>
        </tr>
      </thead>
      <tbody>
        {programs.map((p) => (
          <tr key={p.id}>
            <td className="max-w-xs">
              <Link to={`/programs/${p.slug}`} className="font-medium text-ink hover:text-accent">
                {p.title}
              </Link>
              {p.brand?.slug ? (
                <Link
                  to={`/brands/${p.brand.slug}`}
                  className="mt-0.5 block text-xs text-ink-muted hover:text-ink"
                >
                  {p.brand.companyName}
                </Link>
              ) : (
                // An unpublished brand has no page to link to.
                p.brand && (
                  <span className="mt-0.5 block text-xs text-ink-muted">{p.brand.companyName}</span>
                )
              )}
            </td>
            <td>
              <TermsInline program={p} />
              {p.gifts?.length > 0 && (
                <div className="mt-1">
                  <Badge tone="accent" title="Includes free gifts — see the program page">
                    {p.commissionType === 'gift_only' ? 'Gifts' : 'Gift included'}
                  </Badge>
                </div>
              )}
              {payableCount(p) > 1 && (
                <span className="mt-0.5 block text-xs text-ink-faint">
                  +{payableCount(p) - 1} more paid action{payableCount(p) === 2 ? '' : 's'}
                </span>
              )}
            </td>
            <td className="num">{p.cookieWindowDays}d</td>
            <td className="num">
              {p.averageOrderValueCents ? (
                formatCents(p.averageOrderValueCents, { currency: p.currency })
              ) : (
                <Unknown reason="The brand hasn’t published this" />
              )}
            </td>
            <td className="num">
              {/* Shown as it is. A dash means not enough traffic to say. */}
              {p.performance.conversionRatePct === null ? (
                <Unknown reason="No tracked clicks yet" />
              ) : (
                formatPercent(p.performance.conversionRatePct)
              )}
            </td>
            <td>
              <div className="flex flex-wrap gap-1">
                {(p.platformsWanted ?? []).slice(0, 3).map((pl) => (
                  <Badge key={pl}>{PLATFORM_LABELS[pl] ?? pl}</Badge>
                ))}
                {(p.platformsWanted?.length ?? 0) > 3 && (
                  <Badge>+{p.platformsWanted.length - 3}</Badge>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
